// Read-side helpers + selectors over the DeliveryAnalysisPayload shape.
// Mutations go through the server (PATCH). These power the Delivery view's
// derived data (combined feature list, MU rollups, staleness, etc.).

import type {
  DiscoveryFeature,
  DeliveryAnalysisPayload,
  MigrationUnit,
  FeatureScoring,
} from '../hooks/sessionTypes';
import { UNASSIGNED_MU } from '../hooks/sessionTypes';

const MU_COLORS = ['#4f6ef7', '#16a34a', '#7c3aed', '#d97706', '#ea580c', '#0891b2', '#ec4899', '#6366f1'];

/** Deterministic color from an MU name. Unassigned and empty → neutral slate. */
export function muColor(name: string): string {
  if (!name || name === UNASSIGNED_MU) return '#94a3b8';
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = ((hash << 5) - hash + name.charCodeAt(i)) | 0;
  }
  return MU_COLORS[Math.abs(hash) % MU_COLORS.length];
}

/** Source list = extracted features ⊕ imported features (dedup by candidateId, extracted wins). */
export function combineFeatures(
  extracted: DiscoveryFeature[],
  imported: DiscoveryFeature[],
): DiscoveryFeature[] {
  const extractedIds = new Set(extracted.map((f) => f.candidateId));
  const imports = imported.filter((f) => !extractedIds.has(f.candidateId));
  return [...extracted, ...imports];
}

/** Has the source data drifted since the analysis was last generated? */
export function isStale(
  payload: DeliveryAnalysisPayload,
  currentExtractedAt: string | null,
  currentImportHash: string | null,
): boolean {
  if (!payload.generatedAt) return false;
  if ((payload.basedOnExtractedAt ?? null) !== (currentExtractedAt ?? null)) return true;
  if ((payload.basedOnImportHash ?? null) !== (currentImportHash ?? null)) return true;
  return false;
}

export function isHidden(payload: DeliveryAnalysisPayload, featureId: string): boolean {
  return !!payload.hidden[featureId];
}

/** The MU a feature belongs to, or undefined. O(N*M) — fine for the sizes we expect. */
export function muOfFeature(
  payload: DeliveryAnalysisPayload,
  featureId: string,
): MigrationUnit | undefined {
  return payload.migrationUnits.find((m) => m.featureIds.includes(featureId));
}

/** Color for a feature based on its MU. */
export function colorForFeature(
  payload: DeliveryAnalysisPayload,
  featureId: string,
): string {
  return muColor(muOfFeature(payload, featureId)?.name || UNASSIGNED_MU);
}

/** Look up scoring for a feature; returns empty object if none recorded. */
export function scoringFor(
  payload: DeliveryAnalysisPayload,
  featureId: string,
): FeatureScoring {
  return payload.featureScoring[featureId] || {};
}

export interface MuRollup {
  mu: MigrationUnit;
  color: string;
  features: DiscoveryFeature[];
  visibleFeatures: DiscoveryFeature[];
  totalEffort: number;
  hiddenCount: number;
}

/**
 * Project MUs onto the feature list. Features not in any MU end up in a
 * synthetic Unassigned bucket so nothing falls off the list. MUs whose
 * featureIds reference deleted features get dropped (their content is gone).
 */
export function rollupMus(
  features: DiscoveryFeature[],
  payload: DeliveryAnalysisPayload,
): MuRollup[] {
  const byId = new Map(features.map((f) => [f.candidateId, f]));
  const used = new Set<string>();
  const out: MuRollup[] = [];

  for (const mu of payload.migrationUnits) {
    const muFeatures: DiscoveryFeature[] = [];
    for (const id of mu.featureIds) {
      const f = byId.get(id);
      if (!f) continue;
      muFeatures.push(f);
      used.add(id);
    }
    // Empty MUs are valid state — the user can create one manually and
    // drag features into it later. Only the synthetic Unassigned bucket
    // collapses when empty (handled below by simply not appending it).
    const visible = muFeatures.filter((f) => !payload.hidden[f.candidateId]);
    const totalEffort = visible.reduce((s, f) => s + (scoringFor(payload, f.candidateId).effort || 0), 0);
    out.push({
      mu,
      color: muColor(mu.name),
      features: muFeatures,
      visibleFeatures: visible,
      totalEffort,
      hiddenCount: muFeatures.length - visible.length,
    });
  }

  const orphan = features.filter((f) => !used.has(f.candidateId));
  if (orphan.length > 0) {
    const visible = orphan.filter((f) => !payload.hidden[f.candidateId]);
    const totalEffort = visible.reduce((s, f) => s + (scoringFor(payload, f.candidateId).effort || 0), 0);
    out.push({
      mu: {
        name: UNASSIGNED_MU,
        description: 'Features awaiting a migration unit assignment.',
        comments: '',
        dependsOn: [],
        featureIds: orphan.map((f) => f.candidateId),
        flowText: '',
        flowGeneratedAt: null,
      },
      color: muColor(UNASSIGNED_MU),
      features: orphan,
      visibleFeatures: visible,
      totalEffort,
      hiddenCount: orphan.length - visible.length,
    });
  }

  return out;
}

/** Pipeline-status totals for the Delivery view header. */
export interface PipelineStats {
  muCount: number;
  featureCount: number;
  visibleFeatureCount: number;
  totalEffort: number;
  musWithFlow: number;
  // Spec stats are added in a later phase; left as 0 placeholders here so
  // the header can render without referencing a different payload yet.
  specsGenerated: number;
}

export function computeStats(
  features: DiscoveryFeature[],
  payload: DeliveryAnalysisPayload,
): PipelineStats {
  const visibleFeatureCount = features.filter((f) => !payload.hidden[f.candidateId]).length;
  const totalEffort = features
    .filter((f) => !payload.hidden[f.candidateId])
    .reduce((s, f) => s + (scoringFor(payload, f.candidateId).effort || 0), 0);
  const musWithFlow = payload.migrationUnits.filter((m) => m.flowText && m.flowText.trim().length > 0).length;
  return {
    muCount: payload.migrationUnits.length,
    featureCount: features.length,
    visibleFeatureCount,
    totalEffort,
    musWithFlow,
    specsGenerated: 0,
  };
}
